/**
 * 新房购置一件事 - 接口封装
 * 接口来自桌面《新房购置一件事.postman_collection.json》
 *
 * 不用项目里的 escon request 实例（不便加拦截器），自己用 axios
 */
import axios from 'axios';
import { ElMessage, ElLoading } from 'element-plus';
import { getLocalStore } from '../utils/local-store';
import RestUrl from '../config/rest-url';

const http = axios.create({
  baseURL: '',
  timeout: 30000,
});

// token 拦截器
const TENANT_ID = '1';
http.interceptors.request.use((config) => {
  // escon 的 LocalStore 带前缀 'layout-ms-example-'，要用 getLocalStore 取
  const token = getLocalStore('token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  // TENANT_ID 也从 LocalStore 取，跟 escon 行为一致
  const tenantId = getLocalStore('tenantId') || TENANT_ID;
  config.headers['TENANT-ID'] = tenantId;
  config.headers['X-Forwarded-For'] = '1.1.1.1';
  return config;
});

// 全局 loading
let loadingInstance = null;
let loadingCount = 0;

const showLoading = () => {
  loadingCount += 1;
  if (loadingInstance) return;
  loadingInstance = ElLoading.service({
    lock: true,
    text: '加载中...',
    background: 'rgba(255, 255, 255, 0.6)',
  });
};

const hideLoading = () => {
  loadingCount = Math.max(0, loadingCount - 1);
  if (loadingCount === 0 && loadingInstance) {
    loadingInstance.close();
    loadingInstance = null;
  }
};

// 响应统一处理
const handleResponse = (res) => {
  hideLoading();
  const data = res.data;
  if (data?.code === -1) {
    const msg = data.msg || '接口请求异常';
    ElMessage.error(msg);
    return Promise.reject(new Error(msg));
  }
  if (data?.code && data.code !== 0) {
    // 业务异常（如维修资金已缴返回 code:1）
    return data;
  }
  return data;
};

const handleError = (err) => {
  hideLoading();
  const msg = err?.response?.data?.msg || err?.message || '接口异常';
  ElMessage.error(msg);
  return Promise.reject(err);
};

http.interceptors.response.use(handleResponse, handleError);

// 给每次调用包一层 loading 开关
const withLoading = (config, isUpload = false) => {
  if (isUpload) return config;
  showLoading();
  return config;
};

const BASE_HEADERS = {
  'X-Forwarded-For': '1.1.1.1',
};

/**
 * 1. 用户同步：拿 Bearer Token
 */
export const syncUserRe = (data) => {
  return http.request({
    url: RestUrl.newHouse.syncUser,
    method: 'post',
    headers: { ...BASE_HEADERS, isToken: false },
    data,
    transformRequest: [(d) => d],
  });
};

/**
 * 2. 待办事项列表
 */
export const getTodoListRe = (data = {}) => {
  return http.request({
    url: RestUrl.newHouse.todoList,
    method: 'get',
    headers: BASE_HEADERS,
    data,
  });
};

/**
 * 3. 个人合同列表
 */
export const getMyContractListRe = (data = {}) => {
  return http.request({
    url: RestUrl.newHouse.myContractList,
    method: 'get',
    headers: BASE_HEADERS,
    data: {
      htbh: data.htbh || '',
      dbsxIds: data.dbsxIds || [],
    },
  });
};

/**
 * 4. 业务创建
 */
export const createBusinessRe = (data) => {
  return http.request({
    url: RestUrl.newHouse.createBusiness,
    method: 'post',
    headers: BASE_HEADERS,
    data,
  });
};

/**
 * 5. 业务更新
 * ywgx 接口，ywId 通过 query 传，body 是 detail 整包
 */
export const updateBusinessRe = (id, data) => {
  return http.request({
    url: RestUrl.newHouse.updateBusiness,
    method: 'post',
    headers: BASE_HEADERS,
    params: { id },
    data,
  });
};

/**
 * 6. 业务查询
 */
export const getBusinessDetailRe = (id) => {
  return http.request({
    url: RestUrl.newHouse.businessDetail,
    method: 'get',
    headers: BASE_HEADERS,
    params: { id },
  });
};

/**
 * 7. 个人业务列表
 */
export const getMyBusinessListRe = (data = {}) => {
  return http.request({
    url: RestUrl.newHouse.myBusinessList,
    method: 'get',
    headers: BASE_HEADERS,
    data,
  });
};

/**
 * 8. 维修资金缴款
 */
export const getMaintenanceFundRe = (jkid) => {
  return http.request({
    url: RestUrl.newHouse.maintenanceFund,
    method: 'get',
    headers: BASE_HEADERS,
    params: { jkid },
  });
};

/**
 * 9. 附件上传
 */
export const uploadFileRe = (formData) => {
  return http.request({
    url: RestUrl.newHouse.uploadFile,
    method: 'post',
    headers: { 'Content-Type': 'multipart/form-data' },
    data: formData,
  });
};

/**
 * 10. 附件下载
 */
export const downloadFileRe = (url) => {
  return http.request({
    url: RestUrl.newHouse.downloadFile,
    method: 'post',
    headers: BASE_HEADERS,
    data: { url },
    responseType: 'blob',
  });
};

/**
 * 11. 附件删除
 */
export const removeFileRe = (id) => {
  return http.request({
    url: RestUrl.newHouse.removeFile,
    method: 'post',
    headers: BASE_HEADERS,
    params: { id },
  });
};

// 给 axios 请求包装：每次请求前 showLoading
http.interceptors.request.use(
  (config) => {
    const isUpload =
      config.headers?.['Content-Type']?.includes('multipart/form-data');
    withLoading(config, isUpload);
    return config;
  },
  (err) => {
    hideLoading();
    return Promise.reject(err);
  }
);
